import React, { useEffect, useMemo, useRef, useState } from 'react'
import ZoneAdditionForm from './ZoneAdditionForm'
import { Modal, OverlayTrigger } from 'react-bootstrap';
import { AddNode, ClearFilter, FilterIcon, RemoveIcon } from '../../../assets/images';
import { MaterialReactTable } from 'material-react-table';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Button, Tooltip, CircularProgress, Box } from '@mui/material';
import AlertTemplateMessage from '../../Configuration/AlertTemplate/AlertTemplateMessage';
import { MultiSelect } from 'react-multi-select-component';
import Swal from 'sweetalert2';
import filtersConfig from '../Filters'
import { deleteZoneDetails, getAllFilters, getAllZoneList } from '../../../services/configurationServices'
import { SuccessToast } from '../../../CustomHooks/SuccessToast';

const ZoneConfig = ({showSuccessToast}) => {
    const [showAddSite, setShowAddSite] = useState(false);
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedSite, setSelectedSite] = useState(null);
    const wrapperRef = useRef(null);
    const [filterValues, setFilterValues] = useState({});
    const [filterData, setFilterData] = useState(null);
    const [zoneData, setZoneData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });
    const [globalFilter, setGlobalFilter] = useState('');

    // Enhanced configuration with extensible field mappings
    const getPageConfiguration = () => {
        const pageConfig = filtersConfig.find(config => config.pagename === 'ZoneConfiguration');
        if (!pageConfig) {
            console.warn('ZoneConfiguration not found in filters config');
            return { filters: {}, fieldMapping: {}, displayConfig: {} };
        }
        return pageConfig;
    };

    const pageConfig = getPageConfiguration();
    const pageFilters = pageConfig.filters;
    
    // Dynamic field mapping configuration - can be moved to external config
    const defaultFieldMapping = {
        'Customer': 'ClientName',
        'State': 'statename', 
        'City': 'City',
        'Site': 'SiteName',
        'Zone': 'ZoneName',
        'Interface': 'InterfaceName',
        'Device': 'DeviceName'
    };

    // Use configured field mapping or fall back to default
    const fieldMapping = pageConfig.fieldMapping || defaultFieldMapping;

    // Get the actual field name from configuration (with fallback to default mapping)
    const getFieldName = (filterKey) => {
        const filterConfig = pageFilters[filterKey];
        if (filterConfig && filterConfig.fieldName) {
            return filterConfig.fieldName;
        }
        return fieldMapping[filterKey] || filterKey;
    };

    // Get placeholder text from configuration
    const getPlaceholderText = (filterKey, filterConfig) => {
        if (filterConfig.placeholder) {
            return filterConfig.placeholder;
        }
        return `Select ${filterConfig.label}${filterConfig.mandatory ? '*' : ''}`;
    };
    
    // Excel-like Filter Utility - filters options based on other active selections
    const getFilteredOptions = (filterKey, optionKey, labelKey) => {
        if (!filterData || !filterData.Data) {
            return [];
        }

        // Build a filter object with only selected values for each filter (excluding current filter)
        const activeSelections = {};
        Object.keys(pageFilters).forEach(key => {
            const selected = filterValues[key];
            if (
                key !== filterKey &&
                Array.isArray(selected) &&
                selected.length > 0
            ) {
                // For State filter, we need to use the statemasterid for comparison
                if (key === 'State') {
                    activeSelections[key] = selected.map(item => 
                        typeof item === "object" ? item.value : item
                    );
                } else {
                    activeSelections[key] = selected.map(item => 
                        typeof item === "object" ? item.value : item
                    );
                }
            }
        });

        // Filter data based on all other active selections
        const filtered = filterData.Data.filter(item => {
            return Object.entries(activeSelections).every(([key, selectedVals]) => {
                // Use the correct field for comparison
                let compareField;
                if (key === 'Customer') {
                    compareField = 'ClientId';
                } else if (key === 'State') {
                    compareField = 'statemasterid';
                } else if (key === 'City') {
                    compareField = 'City';
                } else if (key === 'Site') {
                    compareField = 'SiteID';
                } else {
                    compareField = getFieldName(key);
                }
                return selectedVals.includes(item[compareField]);
            });
        });

        // Get unique options for this filter
        const seen = new Set();
        return filtered
            .filter(item => {
                const val = item[optionKey];
                if (!val || seen.has(val)) return false;
                seen.add(val);
                return true;
            })
            .map(item => ({
                label: item[labelKey],
                value: item[optionKey],
            }))
            .sort((a, b) => {
                // Sort options in ascending order by label
                return a.label.localeCompare(b.label, undefined, { 
                    numeric: true, 
                    sensitivity: 'base' 
                });
            });
    };

    // Create options from API filterData with Excel-like filtering - now fully dynamic
    const getFilterOptions = (filterKey) => {
        if (!filterData || !filterData.Data) {
            return [];
        }
        
        let optionKey, labelKey;
        
        if (filterKey === 'Customer') {
            // For Customer, use ClientId as value and ClientName as label
            optionKey = 'ClientId';
            labelKey = 'ClientName';
        } else if (filterKey === 'State') {
            // For State, use statemasterid as value and statename as label
            optionKey = 'statemasterid';
            labelKey = 'statename';
        } else if (filterKey === 'City') {
            // For City, use City as both value and label
            optionKey = 'City';
            labelKey = 'City';
        } else if (filterKey === 'Site') {
            // For Site, use SiteID as value and SiteName as label
            optionKey = 'SiteID';
            labelKey = 'SiteName';
        } else {
            const fieldName = getFieldName(filterKey);
            optionKey = fieldName;
            labelKey = fieldName;
        }
        
        return getFilteredOptions(filterKey, optionKey, labelKey);
    };

    const handleFilterChange = (filterKey, value) => {
        if(filterKey === "Site" && value.length > 0){
            setSelectedSite(value[0])
        }
        setFilterValues(prev => {
            const newValues = {
                ...prev,
                [filterKey]: value
            };

            // Smart dependency clearing - clear dependent filters when parent changes
            const filterConfig = pageFilters[filterKey];
            if (filterConfig) {
                // Find and clear all dependent filters
                Object.entries(pageFilters).forEach(([key, config]) => {
                    if (config.parentFilter === filterKey) {
                        newValues[key] = [];
                        // Recursively clear dependencies
                        const clearDependencies = (parentKey) => {
                            Object.entries(pageFilters).forEach(([depKey, depConfig]) => {
                                if (depConfig.parentFilter === parentKey) {
                                    newValues[depKey] = [];
                                    clearDependencies(depKey);
                                }
                            });
                        };
                        clearDependencies(key);
                    }
                });
            }

            return newValues;
        });

        // Only reset table state when mandatory filters are changed
        const filterConfig = pageFilters[filterKey];
        if (filterConfig && filterConfig.mandatory) {
            // Reset table state when mandatory filters change
            setZoneData([]);
            setFiltersApplied(false);
            setPagination({
                pageIndex: 0,
                pageSize: 10,
            });
        }
    };

    // Apply filters function - now calls API and stores response
    const handleApplyFilters = async () => {
        if (!isMandatoryFiltersMissing()) {
            setLoading(true);
            setFiltersApplied(true);
            try {
                // Prepare filter parameters from selected values
                const filterParams = {};
                Object.entries(filterValues).forEach(([key, value]) => {
                    if (value && value.length > 0) {
                        if (key === 'State') {
                            // For State, pass the statemasterid (ID value) instead of text
                            filterParams['StateID'] = value[0]?.value || value[0];
                        } else if (key === 'City') {
                            filterParams['City'] = value[0]?.value || value[0];
                        } else if (key === 'Customer') {
                            // For Customer, pass the ClientId (ID value) instead of text
                            filterParams['CustomerID'] = value[0]?.value || value[0];
                        } else if (key === 'Site') {
                            // For Site, pass the SiteID (ID value) instead of text
                            filterParams['SiteID'] = value[0]?.value || value[0];
                        } else {
                            const fieldName = getFieldName(key);
                            filterParams[fieldName] = value[0]?.value || value[0];
                        }
                    }
                });

                const response = await getAllZoneList(filterParams);
                if (response && response.Data) {
                    setZoneData(response.Data);
                
                } else {
                    setZoneData([]);
                    console.warn('No zone data received:', response);
                }
            } catch (error) {
                console.error('Error loading zones:', error);
                setZoneData([]);
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {    
        fetchFilters();
    }, []);

    const fetchFilters = async () => {
            try {
                const response = await getAllFilters({ Entity: 'Zone' });
                setFilterData(response);
                
            } catch (error) {
                console.error('Error loading zone filters:', error);
                setFilterData(null);
            }
        };

    // Enhanced validation with better error messaging
    const isMandatoryFiltersMissing = () => {
        return Object.entries(pageFilters).some(([filterKey, filterConfig]) => {
            return filterConfig.mandatory && (!filterValues[filterKey] || filterValues[filterKey].length === 0);
        });
    };

    // Get missing mandatory filters for better user feedback
    const getMissingMandatoryFilters = () => {
        return Object.entries(pageFilters)
            .filter(([filterKey, filterConfig]) => {
                return filterConfig.mandatory && (!filterValues[filterKey] || filterValues[filterKey].length === 0);
            })
            .map(([filterKey, filterConfig]) => filterConfig.label);
    };

    // Enhanced filter button tooltip
    const getFilterButtonTooltip = () => {
        const missingFilters = getMissingMandatoryFilters();
        if (missingFilters.length > 0) {
            return `Please select: ${missingFilters.join(', ')}`;
        }
        return "Apply Filter";
    };

    // Clear all filters function
    const handleClearAllFilters = () => {
        setFilterValues({});
        setZoneData([]);
        setFiltersApplied(false);
        setGlobalFilter('');
        setPagination({
            pageIndex: 0,
            pageSize: 10,
        });
    };

    // Handle global filter change with pagination reset
    const handleGlobalFilterChange = (value) => {
        setGlobalFilter(value);
        // Reset to first page whenever search changes
        setPagination(prev => ({
            ...prev,
            pageIndex: 0
        }));
    };

    // Column mappings for consistent column ordering and display
    const columnMappings = {
        'ZoneName': { header: 'Zone Name', size: 250 },
        'SiteName': { header: 'Site Name', size: 250 },
        'ClientName': { header: 'Customer Name', size: 250 },
        'countryname': { header: 'Country', size: 180 },
        'statename': { header: 'State', size: 200 },
        'City': { header: 'City', size: 150 },
        'Address': { header: 'Address', size: 380 },
        'Region': { header: 'Region', size: 200 },
        'PinCode': { header: 'Pin Code', size: 200 },
        'Area': { header: 'Area', size: 200 },
        'BranchID': { header: 'Branch ID', size: 200 },
    };

    // useEffect(() => {
    //     const fetchFilters = async () => {
    //         try {
    //             const response = await getAllFilters({ Entity: 'Zone' });
    //             setFilterData(response);
    //             console.log('Zone filter data loaded:', response);
    //         } catch (error) {
    //             console.error('Error loading zone filters:', error);
    //             setFilterData(null);
    //         }
    //     };
        
    //     fetchFilters();
    // }, []);

    const showZoneForm = (e, data) => {
        setShowAddSite(true);
        setSelectedRow(data)
        // You can set selectedRow if needed: setSelectedRow(data)
    }

    const columns = useMemo(() => {
        // Dynamic columns based on API response with ordered column creation
        const dynamicColumns = [];
        
        if (zoneData.length > 0) {
            const sampleData = zoneData.slice(0, 5);
            const allKeys = new Set();
            sampleData.forEach(record => {
                Object.keys(record).forEach(key => allKeys.add(key));
            });

            // First, add columns in the order defined by columnMappings
            Object.keys(columnMappings).forEach(key => {
                if (allKeys.has(key)) {
                    const mapping = columnMappings[key];
                    dynamicColumns.push({
                        accessorKey: key,
                        header: mapping.header,
                        size: mapping.size,
                    });
                }
            });

            // Then add any remaining columns not in columnMappings
            const excludeKeys = ['ClientId', 'SiteID', 'ZoneID', 'AddressId', 'ID', 'CustomerID', 
                'ClientID', 'CreatedDate', 'ModifiedDate', 'IsActive', 'countrymasterid','statemasterid', 'SiteModeID', 'SiteMode'];
            Array.from(allKeys).forEach(key => {
                if (!excludeKeys.includes(key) && key !== 'actions' && !columnMappings[key]) {
                    dynamicColumns.push({
                        accessorKey: key,
                        header: key.replace(/([A-Z])/g, ' $1').trim(), // Convert camelCase to readable format
                        size: key === 'ZoneName' || key === 'CustomerName' || key === 'ClientName' || key === 'SiteName' ? 250 : 150,
                    });
                }
            });
        } else {
            // Default columns when no data - use columnMappings order
            Object.keys(columnMappings).forEach(key => {
                const mapping = columnMappings[key];
                dynamicColumns.push({
                    accessorKey: key,
                    header: mapping.header,
                    size: mapping.size,
                });
            });
        }

        // Always add actions column at the end
        dynamicColumns.push({
            accessorKey: "actions",
            header: "Actions",
            enableColumnActions: false,
            enableSorting: false,              
            enableColumnOrdering: false,
            Cell: ({ row }) => (
                <div className="ActionBtns">
                    <Tooltip title="Edit" placement="bottom">
                        <button className="DataAccessEdit borderButton" onClick={(e) => showZoneForm(e, row.original)}>
                            <EditNoteIcon className="ActionsIcon" />
                        </button>
                    </Tooltip>

                    <Tooltip title="Delete" placement="bottom">
                        <button className="DataAccessEdit borderButton" onClick={e => onDeleteZone(e, row.original)}>
                            <DeleteForeverIcon className="ActionsIcon" />
                        </button>
                    </Tooltip>
                </div>
            ),
            muiTableHeadCellProps: {
                sx: {
                position: "sticky",
                right: 0,
                backgroundColor: "#064dc3",
                borderTopRightRadius : "20px",
                color: "white",
                fontWeight: "bold",
                zIndex: 3,
                textAlign: "center",
                },
            },
            muiTableBodyCellProps: {
                sx: {
                position: "sticky",
                right: 0,
                zIndex: 2,
                textAlign: "center",
                },
            },
        });

        return dynamicColumns;
    }, [zoneData]);

    const pagedData = useMemo(() => {
        let filteredData = zoneData;
        
        // Apply global filter to complete dataset first
        if (globalFilter) {
            filteredData = zoneData.filter(item => 
                Object.values(item).some(value => 
                    String(value).toLowerCase().includes(String(globalFilter).toLowerCase())
                )
            );
        }
        
        // Then apply pagination to filtered data
        const start = pagination.pageIndex * pagination.pageSize;
        const end = start + pagination.pageSize;
        return filteredData.slice(start, end);
    }, [zoneData, pagination, globalFilter]);

    // Get filtered count for pagination
    const filteredCount = useMemo(() => {
        if (!globalFilter) return zoneData.length;
        
        return zoneData.filter(item => 
            Object.values(item).some(value => 
                String(value).toLowerCase().includes(String(globalFilter).toLowerCase())
            )
        ).length;
    }, [zoneData, globalFilter]);

    const onBulkDeleteSite = (e) => {
        e.stopPropagation()
        Swal.fire({
            title: "Are you sure?",
            text: "Do you want to delete this zone?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#027aaa",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, Delete!"
        }).then((result) => {
            if (result.isConfirmed) {

            }
        })
    }

   function onDeleteZone(e, row) {
        e.stopPropagation();
        Swal.fire({
        title: "Are you sure?",
        text: `You really want to delete "${row.ZoneName || row.zoneName}" zone!`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
        allowOutsideClick: false,
        }).then((result) => {
        if (result.isConfirmed) {
            deleteSiteZone(row.ZoneID || row.SiteID);
        }
        });
    }

    const deleteSiteZone = async (zoneId) => {
        let userId = localStorage.getItem("UserID");
        await deleteZoneDetails(userId, zoneId).then((resp) => {
            if (resp.Status == "success") {
            SuccessToast(resp.Message);
            fetchFilters();
            handleApplyFilters(); // Refresh the zone list
            return;
            } else if (resp.Status == "failed") {
            Swal.fire({ text: resp.Message });
            } else {
            Swal.fire({ text: "Something went wrong, please try again later!" });
            }
        });
    };

    useEffect(() => {
        if (wrapperRef.current) {
            const labelEl = wrapperRef.current.querySelector(
                ".dropdown-heading .dropdown-heading-value"
            );
            if (labelEl) {
                const firstFilterKey = Object.keys(pageFilters)[0];
                const firstSelection = filterValues[firstFilterKey];
                const selectedLabel = firstSelection?.[0]?.label || "";
                const isTruncated = labelEl.scrollWidth > labelEl.clientWidth;
                labelEl.setAttribute("title", isTruncated ? selectedLabel : "");
            }
        }
    }, [filterValues]);

    return (
        <>
            <div className='p-3'>
                {showAddSite ? (
                    <ZoneAdditionForm showAddSite={showAddSite} setShowAddSite={setShowAddSite} selectedRow={selectedRow} selectedSite={selectedSite} handleApplyFilters={handleApplyFilters} fetchFilters={fetchFilters} />
                ) : (
                    <div className="SiteMainContainer SiteTableMainDiv">
                        <div className="DashboardFilterMainDiv">
                            <div className="RefreshText" style={{ display: "flex" }}>
                                {Object.entries(pageFilters)
                                    .sort(([, a], [, b]) => a.sequence - b.sequence)
                                    .map(([filterKey, filterConfig]) => (
                                        <div key={filterKey} className="inputTypeLabelContainer">
                                            <label className="formFieldLabels">
                                                {filterConfig.label}
                                                {filterConfig.mandatory && <span style={{ color: 'red' }}>*</span>}
                                            </label>
                                            <div ref={wrapperRef}>
                                                <MultiSelect
                                                    key={filterValues[filterKey]?.[0]?.value}
                                                    options={getFilterOptions(filterKey)}
                                                    value={filterValues[filterKey] || []}
                                                    onChange={(value) => handleFilterChange(filterKey, value.length > 1 ? [value[value.length - 1]] : value)}
                                                    labelledBy={`Select ${filterConfig.label}`}
                                                    hasSelectAll={false}
                                                    isCreatable={false}
                                                    disableSearch={false}
                                                    className="SiteConfigSingleSelectDropdownContainer"
                                                    overrideStrings={{
                                                        selectSomeItems:
                                                            (filterValues[filterKey] && filterValues[filterKey][0] && filterValues[filterKey][0].label)
                                                                ? filterValues[filterKey][0].label
                                                                : getPlaceholderText(filterKey, filterConfig),
                                                        allItemsAreSelected:
                                                            (filterValues[filterKey] && filterValues[filterKey][0] && filterValues[filterKey][0].label)
                                                                ? filterValues[filterKey][0].label
                                                                : getPlaceholderText(filterKey, filterConfig),
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                <div className="HMFilterDiv">
                                    <Tooltip title={getFilterButtonTooltip()} placement="bottom">
                                        <button 
                                            className="IconsButtonDiv" 
                                            name="filterBtn"
                                            disabled={isMandatoryFiltersMissing() || loading}
                                            style={{ 
                                                opacity: (isMandatoryFiltersMissing() || loading) ? 0.5 : 1,
                                                cursor: (isMandatoryFiltersMissing() || loading) ? 'not-allowed' : 'pointer'
                                            }}
                                            onClick={handleApplyFilters}
                                        >
                                            <img
                                                src={FilterIcon}
                                                className="camera-icon-height"
                                                alt="Apply filter icon"
                                            />
                                        </button>
                                    </Tooltip>
                                    <Tooltip title="Clear Filter" placement="bottom">
                                        <button 
                                            className="IconsButtonDiv" 
                                            name="clearFilterBtn"
                                            disabled={loading}
                                            style={{ 
                                                opacity: loading ? 0.5 : 1,
                                                cursor: loading ? 'not-allowed' : 'pointer'
                                            }}
                                            onClick={handleClearAllFilters}
                                        >
                                            <img
                                                src={ClearFilter}
                                                className="camera-icon-height"
                                                alt="Clear filter icon"
                                            />
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                        <div className="SiteMainHeightDiv">
                            <div className="col-lg-12 m-0 TabulerReportMainDiv MatReactTableDiv" style={{ height: "calc(100vh - 290px)" }}>
                                <div className="card-body p-0">
                                    {loading ? (
                                        <Box 
                                            display="flex" 
                                            justifyContent="center" 
                                            alignItems="center" 
                                            minHeight="400px"
                                            flexDirection="column"
                                        >
                                            <CircularProgress size={60} thickness={4} />
                                            <Box mt={2}>
                                                <span style={{ fontSize: '16px', color: '#666' }}>Loading zone data...</span>
                                            </Box>
                                        </Box>
                                    ) : zoneData.length === 0 ? (
                                        <div style={{ width: "100%", padding: "20px", textAlign: "center" }}>
                                            <AlertTemplateMessage
                                                message={
                                                    !filtersApplied || isMandatoryFiltersMissing()
                                                        ? "Please select mandatory filters and click Apply Filter to load zone data."
                                                        : "No zones found for the selected filters. Please add a zone"
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <MaterialReactTable
                                            columns={columns}
                                            data={pagedData}
                                            enableGlobalFilter={true}
                                            enableColumnFilters={false}
                                            enableStickyHeader
                                            enableColumnResizing={true}
                                            globalFilterFn="contains"
                                            columnResizeMode="onEnd"
                                            enableColumnOrdering={true}
                                            enableRowSelection={true}
                                            muiTableHeadCellProps={{
                                                className: "myHeaderSelectAll",
                                            }}
                                            enableHiding={true}
                                            displayColumnDefOptions={{
                                               'mrt-row-select': { 
                                                    visibleInShowHideMenu: false,
                                                    muiTableHeadCellProps: {
                                                        sx: {
                                                            position: "sticky",
                                                            left: 0,
                                                            backgroundColor: "#0b70d1",
                                                            borderTopLeftRadius: "20px",
                                                            color: "white",
                                                            fontWeight: "bold",
                                                            zIndex: 5,
                                                        },
                                                    },
                                                    muiTableBodyCellProps: {
                                                        sx: {
                                                            position: "sticky",
                                                            left: 0,
                                                            backgroundColor: "inherit",
                                                            zIndex: 4,
                                                        },
                                                    },
                                                },
                                            }}
                                            initialState={{ showColumnFilters: false }}
                                            manualPagination={true}
                                            manualFiltering={true}
                                            rowCount={filteredCount}
                                            getRowId={(row) => row.ZoneID}
                                            state={{
                                                pagination: pagination,
                                                globalFilter: globalFilter,
                                            }}
                                            onPaginationChange={setPagination}
                                            onGlobalFilterChange={handleGlobalFilterChange}
                                            muiTableContainerProps={{
                                                className: "MaterialTableJs myTableContainer",
                                                sx: {
                                                    maxHeight: "100%",
                                                    overflow: "auto",
                                                },
                                            }}
                                            muiTableHeadProps={{
                                                className: "myHeader",
                                                sx: {
                                                    display: "table-header-group",
                                                },
                                            }}
                                            renderTopToolbarCustomActions={({ table }) => (
                                                <div className='d-flex justify-content-center align-items-center'>
                                                    <h4 style={{ margin: 0 }}>
                                                        List of zones {zoneData.length > 0 && `(${zoneData.length} records)`}
                                                    </h4>
                                                </div>
                                            )}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className='mb-2 mt-2'>
                            <Button className='m-1 ConfigBelowButtons' variant="contained" onClick={showZoneForm} disabled={!filtersApplied}>Add Zone</Button>
                            {/* <Button className='m-1' variant="contained" onClick={() => setShowBulkEditModal(true)}>Bulk Edit</Button>
                            <Button className='m-1' variant="contained" onClick={onBulkDeleteSite}>Bulk Delete</Button> */}
                        </div>
                    </div>
                )}
            </div>

            {/* Bulk Edit Modal */}
            <Modal
                show={showBulkEditModal}
                onHide={() => setShowBulkEditModal(false)}
                dialogClassName="widgetModelDiv"
                aria-labelledby="example-custom-modal-styling-title"
            >
                <>
                    <Modal.Header>
                        <Modal.Title id="example-custom-modal-styling-title">
                            Zone Bulk Edit
                        </Modal.Title>
                        <img
                            src={RemoveIcon}
                            alt="Remove"
                            style={{ cursor: "pointer" }}
                            onClick={() => { setShowBulkEditModal(false) }}
                        />
                    </Modal.Header>

                    <Modal.Body>
                        <div className='SiteFormAdditionForm border-top-0'>
                            <form style={{ width: "100%" }}>
                                <div className="site-form">
                                    <div className="inputMainContainer w40dot75rem">
                                        <div className="inputTypeLabelContainer">
                                            <label className='formFieldLabels'>Zone Name</label>
                                            <input
                                                className="inputType"
                                                placeholder="Zone Name"
                                            />
                                            <p className="Error_P" style={{ float: "left" }}><span>{ }</span></p>
                                        </div>
                                    </div>
                                    <div className="inputMainContainer w40dot75rem mt35">
                                        <div className="inputTypeLabelContainer">
                                            <label className='formFieldLabels'>Latitude</label>
                                            <input
                                                className="inputType"
                                                placeholder="Latitude"
                                            />
                                            <p className="Error_P" style={{ float: "left" }}><span></span></p>
                                        </div>
                                    </div>

                                    <div className="inputMainContainer w40dot75rem mt35">
                                        <div className="inputTypeLabelContainer">
                                            <label className='formFieldLabels'>Longitude</label>
                                            <input
                                                className="inputType"
                                                placeholder="Longitude"
                                            />
                                            <p className="Error_P" style={{ float: "left" }}><span></span></p>
                                        </div>
                                    </div>

                                    <div className="inputMainContainer w40dot75rem mt35">
                                        <div className="inputTypeLabelContainer">
                                            <label className='formFieldLabels'>Address One</label>
                                            <input
                                                className="inputType"
                                                placeholder="Address One"
                                            />
                                            <p className="Error_P" style={{ float: "left" }}><span></span></p>
                                        </div>
                                    </div>
                                    <div className="inputMainContainer w40dot75rem mt35">
                                        <div className="inputTypeLabelContainer">
                                            <label className='formFieldLabels'>Select Region</label>
                                            <input
                                                className="inputType"
                                                placeholder="Region"
                                            />
                                            <p className="Error_P" style={{ float: "left" }}><span></span></p>
                                        </div>
                                    </div>

                                    <div className="inputMainContainer w40dot75rem mt35">
                                        <div className="inputTypeLabelContainer">
                                            <label className='formFieldLabels'>Select Area</label>
                                            <input
                                                className="inputType"
                                                placeholder="Area"
                                            />
                                            <p className="Error_P" style={{ float: "left" }}><span></span></p>
                                        </div>
                                    </div>

                                    <div className="inputMainContainer w40dot75rem mt35">
                                        <div className="inputTypeLabelContainer">
                                            <label className='formFieldLabels'>Select Country</label>
                                            <select className="inputType ArrowSelectClass" disabled={true}>
                                                <option key="0" disabled={false} value="0" id="0">Select Country*</option>
                                                <option value="1">India</option>
                                                <option value="2">USA</option>
                                                <option value="3">UK</option>
                                            </select>
                                            <p className="Error_P" style={{ float: "left" }}><span> </span></p>
                                        </div>
                                    </div>

                                    <div className="inputMainContainer w40dot75rem mt35">
                                        <div className="inputTypeLabelContainer">
                                            <label className='formFieldLabels'>Select State</label>
                                            <select className="inputType ArrowSelectClass" disabled={true}>
                                                <option key="0" disabled={false} value="0">Select State*</option>
                                                <option value="1">Maharashtra</option>
                                                <option value="2">Jharkhand</option>
                                                <option value="3">Uttar Pradesh</option>
                                            </select>
                                            <p className="Error_P" style={{ float: "left" }}><span></span></p>
                                        </div>
                                    </div>

                                    <div className="inputMainContainer w40dot75rem mt35">
                                        <div className="inputTypeLabelContainer">
                                            <label className='formFieldLabels'>Select City</label>
                                            <input
                                                className="inputType"
                                                placeholder="Select City*"
                                            />
                                            <p className="Error_P" style={{ float: "left" }}><span></span></p>
                                        </div>
                                    </div>


                                    <div className="inputMainContainer w40dot75rem mt35">
                                        <div className="inputTypeLabelContainer">
                                            <label className='formFieldLabels'>Pin Code</label>
                                            <input
                                                className="inputType"
                                                placeholder="Pin code"
                                            />
                                            <p className="Error_P" style={{ float: "left" }}><span></span></p>
                                        </div>
                                    </div>

                                    <div className="inputMainContainer w40dot75rem mt35">
                                        <div className="inputTypeLabelContainer">
                                            <label className='formFieldLabels'>Branch ID</label>
                                            <input
                                                className="inputType"
                                                placeholder="Branch Id"
                                            />
                                            <p className="Error_P" style={{ float: "left" }}><span>{ }</span></p>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div style={{ textAlign: "center" }}>
                            <button
                                className="DataAccessEdit mt-3"
                                style={{ width: "100px" }}>
                                <span className="ViewUpdate">Submit</span>
                            </button>
                            <button onClick={() => { setShowBulkEditModal(false) }}
                                className="DataAccessEdit mt-3"
                                style={{ width: "100px" }}>
                                <span className="ViewUpdate">Cancel</span>
                            </button>
                        </div>
                    </Modal.Body>
                </>
            </Modal>
        </>
    )
}

export default ZoneConfig